import type { GameStateManager } from '../game/GameState';
import type { GameData, BreachEscapeResult, GameSpeed } from '../game/types';
import { AudioManager } from '../audio/AudioManager';
import { getAnomalyDisplayName } from '../utils/AnomalyNameResolver';

export class BreachEscapeModal {
  private keyDownHandler: ((e: KeyboardEvent) => void) | null = null;

  constructor(
    private container: HTMLElement,
    private gameState: GameStateManager,
    private gameData: GameData,
    private onClose: () => void,
    private onSpeedRestore?: (speed: GameSpeed) => void
  ) {
    this.setup();
  }

  private setup(): void {
    this.container.addEventListener('click', (e) => {
      if ((e.target as HTMLElement).classList.contains('modal-backdrop')) {
        this.hide();
      }
    });
  }

  private handleKeyDown(event: KeyboardEvent): void {
    if (this.container.classList.contains('hidden')) return;

    if (event.code === 'Enter' || event.code === 'Escape' || event.code === 'Space') {
      event.preventDefault();
      event.stopPropagation();
      this.hide();
    }
  }

  show(result: BreachEscapeResult): void {
    this.render(result);
    this.container.classList.remove('hidden');
    this.gameState.setPausedByModal(true);
    
    AudioManager.getInstance().playEffect();
    
    this.keyDownHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.keyDownHandler);
    
    const modalContent = this.container.querySelector('.modal-content') as HTMLElement;
    if (modalContent) {
      modalContent.focus();
    }
  }

  hide(): void {
    this.container.classList.add('hidden');
    this.gameState.setPausedByModal(false);
    const restoredSpeed = this.gameState.restorePauseStateAfterModal();
    if (restoredSpeed !== null && this.onSpeedRestore) {
      this.onSpeedRestore(restoredSpeed);
    }
    this.gameState.dequeueBreachEscape();
    
    if (this.keyDownHandler) {
      document.removeEventListener('keydown', this.keyDownHandler);
      this.keyDownHandler = null;
    }
    
    // Defer onClose to next tick to prevent synchronous re-showing of same modal
    setTimeout(() => {
      this.onClose();
    }, 0);
  }

  isOpen(): boolean {
    return !this.container.classList.contains('hidden');
  }

  private render(result: BreachEscapeResult): void {
    const strings = this.gameData.uiStrings;

    // Try to find the anomaly to get resolved name
    // Search through all anomalies and breached anomalies
    let displayName = result.anomalyName;
    const state = this.gameState.getState();
    
    // Check normal anomalies
    for (const anomaly of state.anomalies) {
      if (anomaly.data.name === result.anomalyName) {
        displayName = getAnomalyDisplayName(anomaly, this.gameData);
        break;
      }
    }
    
    // Check breached anomalies if not found
    if (displayName === result.anomalyName) {
      for (const breached of state.breachedAnomalies) {
        if (breached.originalAnomaly.data.name === result.anomalyName) {
          displayName = getAnomalyDisplayName(breached.originalAnomaly, this.gameData);
          break;
        }
      }
    }

    this.container.innerHTML = `
      <div class="modal-backdrop"></div>
      <div class="modal-content breach-escape-modal" tabindex="0">
        <div class="modal-header">
          <h2>⚠️ Breach Escape</h2>
          <button class="modal-close" data-action="close">×</button>
        </div>
        <div class="modal-body">
          <div class="breach-escape-info">
            <div class="escape-message">
              <p><strong>${displayName}</strong> has escaped containment!</p>
              <p>External containment teams have been called in to eliminate the threat.</p>
              <p class="cost-warning">Cost: <strong>${result.cost} Budget</strong></p>
              <p class="warning-text">The public remains unaware, but this incident has been costly.</p>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="button-confirm" data-action="close">Acknowledge</button>
        </div>
      </div>
    `;

    const closeButton = this.container.querySelector('.modal-close') as HTMLElement;
    if (closeButton) {
      closeButton.addEventListener('click', () => this.hide());
    }

    const footerCloseButton = this.container.querySelector('.modal-footer [data-action="close"]') as HTMLElement;
    if (footerCloseButton) {
      footerCloseButton.addEventListener('click', () => this.hide());
    }
  }
}
